/**
 * Generic base component for every sub-FAB rendered inside the burst.
 *
 * Two modes are supported through a discriminated union:
 *   - `kind: "action"` — a plain click button firing `onAction`, with no
 *     modal wiring. Used for pure side-effect triggers (e.g. "navigate").
 *   - `kind: "modal"` — clicking toggles a `MiniModal` anchored on this
 *     sub-FAB trigger. The parent burst stays open so the expanded ring
 *     remains visible while the panel is shown.
 *
 * Centralising the discriminated union here lets TypeScript reject
 * invalid combinations at compile time (an action cannot have
 * `children`, a modal cannot forget its `children`, etc.), which
 * minimises errors when adding a new sub-FAB.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getUIColorPalette } from "../../../../theme/ColorPalette";
import MiniModal from "../../../MiniModal/MiniModal";
import { FAB_ICON_SIZE_PX, SUB_FAB_SIZE_PX } from "../../floatingAction.configuration";
import { useFloatingActionBurstContext } from "../FloatingActionBurstContext";

/** Preferred width (px) of the mini-modal opened by a modal sub-FAB. */
const DEFAULT_MODAL_MAX_WIDTH_PX = 300;

/** Common visual properties shared by action and modal sub-FABs. */
interface SubFloatingActionBaseProps {

    /** Image `src` rendered inside the 40x40 wrapper. */
    "iconSrc": string;

    /** Alt text applied to the inner `<img>` for accessibility. */
    "iconAlt": string;

    /** Accessible label applied to the trigger button. */
    "ariaLabel": string;

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;

    /**
     * Optional CSS `filter` applied to the icon image. Allows recolouring
     * a monochrome asset (for example the pricing `ChangeIcon`) without
     * having to duplicate the SVG.
     */
    "iconFilter"?: string;

    /**
     * Optional icon width/height (px). Defaults to `FAB_ICON_SIZE_PX`
     * when omitted so most sub-FABs stay visually aligned.
     */
    "iconSizePx"?: number;
}

/** Props accepted when the sub-FAB only fires a side-effect on click. */
export interface SubFloatingActionActionProps extends SubFloatingActionBaseProps {
    "kind":     "action";
    "onAction": () => void;

    /** Actions never render a panel — `children` must be omitted. */
    "children"?: never;
}

/** Props accepted when the sub-FAB opens a MiniModal on click. */
export interface SubFloatingActionModalProps extends SubFloatingActionBaseProps {
    "kind": "modal";

    /** Optional modal title displayed in the MiniModal header. */
    "modalTitle"?: string;

    /** Optional modal `max-width` (px). Defaults to `DEFAULT_MODAL_MAX_WIDTH_PX`. */
    "modalMaxWidth"?: number;

    /**
     * Optional notifier called whenever the modal open state changes.
     * Used by `Home` for the Bike panel so map-level UI can react.
     */
    "onOpenStateChange"?: ( isOpen: boolean ) => void;

    /** Modal body. Required for modal mode. */
    "children": React.ReactNode;
}

/** Public discriminated union exposed by the component. */
export type SubFloatingActionProps =
  | SubFloatingActionActionProps |
  SubFloatingActionModalProps;

/**
 * Type guard picking the modal branch of the discriminated union. Keeps
 * the narrowing inline-expression readable in the JSX below.
 *
 * @param props Full union props.
 * @returns `true` when the props describe a modal sub-FAB.
 */
const isModalProps = ( props: SubFloatingActionProps ): props is SubFloatingActionModalProps => {
    return props.kind === "modal";
};

/**
 * Sub-FAB base component.
 *
 * @param props Discriminated union of action or modal props.
 * @returns The rendered sub-FAB (with optional anchored MiniModal).
 */
const SubFloatingAction: React.FC<SubFloatingActionProps> = ( props ) => {
    const {
        iconSrc,
        iconAlt,
        ariaLabel,
        isDarkUi,
        iconFilter,
        iconSizePx,
    } = props;
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const buttonRef = useRef<HTMLButtonElement | null>( null );
    const burstContext = useFloatingActionBurstContext();

    const [ isOpen, setIsOpen ] = useState<boolean>( false );

    /** Close the modal. Used by the MiniModal's dismissal callbacks. */
    const closePanel = useCallback( (): void => {
        setIsOpen( false );
    }, [] );

    const onOpenStateChange = isModalProps( props ) ? props.onOpenStateChange : undefined;
    const registerPanelClose = burstContext?.registerPanelClose;
    const requestCloseOpenPanels = burstContext?.requestCloseOpenPanels;

    const isModal = isModalProps( props );

    /*
     * Let the main burst FAB dismiss this panel when the user short- or
     * long-presses the grouped FAB while a MiniModal is still open.
     */
    useEffect( () => {
        if ( isModal === false || registerPanelClose === undefined ) {
            return;
        }

        return registerPanelClose( closePanel );
    }, [ closePanel, isModal, registerPanelClose ] );

    /*
     * Notify the optional external listener when the modal open state
     * changes. The burst is intentionally left open so sibling sub-FABs
     * stay reachable while the panel is visible.
     */
    useEffect( () => {
        if ( onOpenStateChange !== undefined ) {
            onOpenStateChange( isOpen );
        }
    }, [ isOpen, onOpenStateChange ] );

    // Visual wrapper shared by both modes (matches the main FAB footprint).
    const triggerStyle: React.CSSProperties = {
        "backgroundColor": palette.floatingActionButtonBackground,
        "borderRadius":    "50%",
        "width":           `${ SUB_FAB_SIZE_PX }px`,
        "height":          `${ SUB_FAB_SIZE_PX }px`,
    };
    const resolvedIconSizePx = iconSizePx ?? FAB_ICON_SIZE_PX;
    const iconStyle: React.CSSProperties = {
        "width":     `${ resolvedIconSizePx }px`,
        "height":    `${ resolvedIconSizePx }px`,
        "objectFit": "contain",
        ...iconFilter !== undefined ? { "filter": iconFilter } : {},
    };

    /**
     * Modal clicks close siblings first so only one MiniModal is open at a
     * time while the burst stays expanded; a second click on the same
     * trigger closes its panel.
     */
    const handleClick = isModal
        ? (): void => {
            if ( isOpen ) {
                closePanel();

                return;
            }
            requestCloseOpenPanels?.();
            setIsOpen( true );
        }
        : (): void => {
            props.onAction();
        };

    return (
        <div className="flex relative">
            {isModal && (
                <MiniModal
                    anchorRef={buttonRef}
                    isDarkUi={isDarkUi}
                    isOpen={isOpen}
                    maxWidth={props.modalMaxWidth ?? DEFAULT_MODAL_MAX_WIDTH_PX}
                    onClose={closePanel}
                    placement="left"
                    title={props.modalTitle}
                >
                    {props.children}
                </MiniModal>
            )}
            <button
                aria-expanded={isModal ? isOpen : undefined}
                aria-haspopup={isModal ? "true" : undefined}
                aria-label={ariaLabel}
                className="m-1 cursor-pointer shadow-xl flex items-center justify-center"
                onClick={handleClick}
                ref={buttonRef}
                style={triggerStyle}
                type="button"
            >
                <img
                    alt={iconAlt}
                    src={iconSrc}
                    style={iconStyle}
                />
            </button>
        </div>
    );
};

export default SubFloatingAction;
